import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ---- Language plumbing: reads current lang from localStorage on each call ----
// Kept as a plain module-level helper so any non-hook call site can use it.
const _lang = () => {
  try {
    const v = localStorage.getItem("jyotish-lang");
    return v && ["en", "hi", "te"].includes(v) ? v : "en";
  } catch { return "en"; }
};
const _withLang = (params = {}) => ({ ...params, lang: _lang() });

export const api = {
  geocode: (q) => client.get(`/geocode`, { params: { q } }).then(r => r.data),
  listProfiles: () => client.get("/profiles").then(r => r.data),
  createProfile: (data) => client.post("/profiles", data).then(r => r.data),
  patchProfile: (id, data) => client.patch(`/profiles/${id}`, data).then(r => r.data),
  getProfile: (id) => client.get(`/profiles/${id}`).then(r => r.data),
  deleteProfile: (id) => client.delete(`/profiles/${id}`).then(r => r.data),
  chart: (id) => client.get(`/profiles/${id}/chart`).then(r => r.data),
  strengths: (id) => client.get(`/profiles/${id}/strengths`).then(r => r.data),
  dasha: (id) => client.get(`/profiles/${id}/dasha`).then(r => r.data),
  day: (id, date) => client.get(`/profiles/${id}/day`, { params: _withLang({ date }) }).then(r => r.data),
  homeopathy: (id) => client.get(`/profiles/${id}/homeopathy`, { params: _withLang() }).then(r => r.data),
  refreshHomeo: (id) => client.post(`/profiles/${id}/homeopathy/refresh`, null, { params: _withLang() }).then(r => r.data),
  kentian: (id) => client.get(`/profiles/${id}/kentian`, { params: _withLang() }).then(r => r.data),
  refreshKentian: (id) => client.post(`/profiles/${id}/kentian/refresh`, null, { params: _withLang() }).then(r => r.data),
  miasm: (id) => client.get(`/profiles/${id}/miasm`, { params: _withLang() }).then(r => r.data),
  refreshMiasm: (id) => client.post(`/profiles/${id}/miasm/refresh`, null, { params: _withLang() }).then(r => r.data),
  range: (id, range_type, start, end) => client.get(`/profiles/${id}/range`, { params: _withLang({ range_type, start, end }) }).then(r => r.data),
  mahadasha: (id, index) => client.get(`/profiles/${id}/mahadasha`, { params: _withLang({ index }) }).then(r => r.data),
  lifespan: (id) => client.get(`/profiles/${id}/lifespan`, { params: _withLang() }).then(r => r.data),
  redAlerts: (id) => client.get(`/profiles/${id}/red-alerts`, { params: _withLang() }).then(r => r.data),
  panchang: (params) => client.get(`/panchang`, { params }).then(r => r.data),
  attributes: (id) => client.get(`/profiles/${id}/attributes`).then(r => r.data),
  year: (id, year) => client.get(`/profiles/${id}/year`, { params: _withLang({ year }) }).then(r => r.data),
  numerology: (name, dob, include_interpretation = true) =>
    client.post(`/numerology`, { name, dob, include_interpretation, lang: _lang() }).then(r => r.data),
  aiSearch: (question, profile_id = "") =>
    client.post(`/ai-search`, { question, profile_id, lang: _lang() }).then(r => r.data),
  yogas: (id, varga) => {
    const params = _withLang();
    if (varga && varga !== "D1") params.varga = varga;
    return client.get(`/profiles/${id}/yogas`, { params }).then(r => r.data);
  },
  navatharaka: (id) => client.get(`/profiles/${id}/navatharaka`).then(r => r.data),
  chartView: (id, view) => client.get(`/profiles/${id}/chart-view`, { params: _withLang({ view }) }).then(r => r.data),
  bookAppointment: (data) => client.post(`/appointments`, data).then(r => r.data),
  listAppointments: (key) => client.get(`/appointments`, { params: { key } }).then(r => r.data),
  updateAppointment: (id, status, key) => client.patch(`/appointments/${id}`, { status }, { params: { key } }).then(r => r.data),
  deleteAppointment: (id, key) => client.delete(`/appointments/${id}`, { params: { key } }).then(r => r.data),

  // ---- Batch Generate All 143 Full-Life sub-tabs + Profile Photo ----
  batchStart: (id, lang = "en", force = false) =>
    client.post(`/profiles/${id}/full-life/batch`, null, { params: { lang, force } }).then(r => r.data),
  batchResume: (id, lang = "en") =>
    client.post(`/profiles/${id}/full-life/batch/resume`, null, { params: { lang } }).then(r => r.data),
  batchCancel: (id, lang = "en") =>
    client.post(`/profiles/${id}/full-life/batch/cancel`, null, { params: { lang } }).then(r => r.data),

  // ---- Unified "Generate Full Masterpiece" batch (all tabs at once) ----
  masterpieceStart: (id, lang = "en") =>
    client.post(`/profiles/${id}/masterpiece/batch`, null, { params: { lang } }).then(r => r.data),
  masterpieceStatus: (id, lang = "en") =>
    client.get(`/profiles/${id}/masterpiece/batch/status`, { params: { lang } }).then(r => r.data),
  masterpieceCancel: (id, lang = "en") =>
    client.post(`/profiles/${id}/masterpiece/batch/cancel`, null, { params: { lang } }).then(r => r.data),
  masterpieceResume: (id, lang = "en") =>
    client.post(`/profiles/${id}/masterpiece/batch/resume`, null, { params: { lang } }).then(r => r.data),
  masterpieceRetryFailed: (id, lang = "en") =>
    client.post(`/profiles/${id}/masterpiece/batch/retry-failed`, null, { params: { lang } }).then(r => r.data),
  masterpieceScreener: (id, lang = "en") =>
    client.get(`/profiles/${id}/masterpiece/screener`, { params: { lang } }).then(r => r.data),
  masterpieceRegenerateItem: (id, step, slug, lang = "en") =>
    client.post(`/profiles/${id}/masterpiece/screener/regenerate`, null,
      { params: { step, slug, lang } }).then(r => r.data),

  // ---- Strategic Life Map (30-page flagship synthesis) ----
  strategicLifeMapStart: (id, lang = "en") =>
    client.post(`/profiles/${id}/strategic-life-map/start`, null, { params: { lang } }).then(r => r.data),
  strategicLifeMapStatus: (id, lang = "en") =>
    client.get(`/profiles/${id}/strategic-life-map/status`, { params: { lang } }).then(r => r.data),
  strategicLifeMapCancel: (id, lang = "en") =>
    client.post(`/profiles/${id}/strategic-life-map/cancel`, null, { params: { lang } }).then(r => r.data),
  strategicLifeMapPdfUrl: (id, lang = "en") =>
    `${API}/profiles/${id}/strategic-life-map/pdf?lang=${lang}`,
  batchRetryFailed: (id, lang = "en") =>
    client.post(`/profiles/${id}/full-life/batch/retry-failed`, null, { params: { lang } }).then(r => r.data),
  batchStatus: (id, lang = "en") =>
    client.get(`/profiles/${id}/full-life/batch/status`, { params: { lang } }).then(r => r.data),
  uploadProfilePhoto: (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    return client.post(`/profiles/${id}/photo`, fd,
      { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  photoUrl: (id) => `${API}/profiles/${id}/photo`,

  pdfUrl: (id, date) => {
    const qs = new URLSearchParams({ lang: _lang(), ...(date ? { date } : {}) }).toString();
    return `${API}/profiles/${id}/pdf?${qs}`;
  },
  marketForecast: (payload) => client.post(`/market/forecast`, payload).then(r => r.data),
  marketPdfUrl: () => `${API}/market/forecast/pdf`,
  rangeStrengths: (payload) => client.post(`/market/range-strengths`, payload).then(r => r.data),
  rangeStrengthsPdfUrl: () => `${API}/market/range-strengths/pdf`,
  rangeStrengthsCsvUrl: () => `${API}/market/range-strengths/csv`,
  vimshottariCsvUrl: (id) => `${API}/profiles/${id}/vimshottari/csv`,
  cascadeStart: (id) => client.post(`/profiles/${id}/daily-cascade/start`).then(r => r.data),
  cascadeStatus: (jobId) => client.get(`/daily-cascade/status/${jobId}`).then(r => r.data),
  cascadeDownloadUrl: (jobId) => `${API}/daily-cascade/download/${jobId}`,
  cascadeRangePdfUrl: (id) => `${API}/profiles/${id}/daily-cascade/range-pdf`,
  mahadashaList: (id) => client.get(`/profiles/${id}/mahadasha-list`).then(r => r.data),
  dashaAt: (id, date) => client.get(`/profiles/${id}/dasha-at?date=${date}`).then(r => r.data),
  caseList: () => client.get(`/case-records`).then(r => r.data),
  caseGet: (caseId) => client.get(`/case-records/${caseId}`).then(r => r.data),
  caseSave: (payload) => client.post(`/case-records`, payload).then(r => r.data),
  caseDelete: (caseId) => client.delete(`/case-records/${caseId}`).then(r => r.data),
  casePdfUrl: () => `${API}/case-records/pdf`,
  astroTopic: (id, topic, subtopic) =>
    client.post(`/profiles/${id}/astro-topic`, { topic, subtopic, lang: _lang() }).then(r => r.data),
  astroTopicPdfUrl: (id) => `${API}/profiles/${id}/astro-topic/pdf`,
  // Consultations (Razorpay prepaid chat + jitsi call)
  consultOrder: (payload) => client.post(`/consult/order`, payload).then(r => r.data),
  consultVerify: (payload) => client.post(`/consult/verify`, payload).then(r => r.data),
  consultGet: (id) => client.get(`/consult/${id}`).then(r => r.data),
  consultMessages: (id) => client.get(`/consult/${id}/messages`).then(r => r.data),
  consultSend: (id, sender, text, adminKey) =>
    client.post(`/consult/${id}/messages`, {
      sender, text, ...(sender === "doctor" && adminKey ? { admin_key: adminKey } : {}),
    }).then(r => r.data),
  consultAdminList: (adminKey) =>
    client.get(`/consult/admin/list`, { params: { admin_key: adminKey } }).then(r => r.data),
  consultAdminNotifications: (adminKey, bookingId) =>
    client.get(`/consult/admin/notifications`, {
      params: { admin_key: adminKey, ...(bookingId ? { booking_id: bookingId } : {}) },
    }).then(r => r.data),
  consultAdminTestNotify: (adminKey) =>
    client.post(`/consult/admin/test-notify`, null,
      { params: { admin_key: adminKey } }).then(r => r.data),
};

export default api;
